import { Component, inject, OnInit } from '@angular/core';
import { DiscountType } from '../../../../shared/enums/discount-type.enum';
import { AccessGroupService } from '../../../../shared/services/access-group.service';
import { AccessGroup, AccessGroupSearchEvent } from '../../../access-groups/access-group.model';
import { catchError, combineLatest, map, mergeMap, Observable, of, take, tap } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import OrganizationService from '../../../../shared/services/organization.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorMessage } from '../../../../shared/models/errorMessage.model';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { createMask, InputMaskModule } from '@ngneat/input-mask';
import {
  AccessGroupSharedDiscountSearchEvent,
  AccessGroupSharedDiscountService,
  IAccessGroupSharedDiscountSearchResultItem,
} from 'src/app/shared/services/access-group-shared-discount.service';
import { TranslateOrganizationDiscountTypeEnum } from '../../../../shared/pipes/translate-organization-discount-type-enum.pipe';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';

export type UpdateDiscountTypeData = {
  organizationId: number;
  organizationName: string;
  existingDiscountType: DiscountType;
};

export interface IAccessGroupDiscountFormGroup {
  amount: FormControl<number | null>;
  sharedAccessGroupDiscountIdentifier: FormControl<string | null>;
}

@Component({
  selector: 'adm-update-discount-type',
  imports: [
    InputMaskModule,
    TranslateOrganizationDiscountTypeEnum,
    FeedbackInfoComponent,
    ReactiveFormsModule,
    AsyncPipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatSuffix,
    MatInput,
    MatSelect,
    MatOption,
    MatTooltip,
    CustomDropDownArrowDirective,
  ],
  templateUrl: './update-discount-type-dialog.component.html',
  styleUrl: './update-discount-type-dialog.component.scss',
})
export class UpdateDiscountTypeComponent implements OnInit {
  data = inject<UpdateDiscountTypeData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<void>>(MatDialogRef);

  priceInputMask = createMask({
    alias: 'numeric',
    placeholder: '',
    digits: 2,
    min: 0,
    groupSeparator: '',
    radixPoint: ',',
    parser: (value: string) => {
      const parsed = parseFloat(value.replace(',', '.'));
      return isNaN(parsed) ? null : parsed;
    },
  });

  public accessGroups$: Observable<AccessGroup[]>;

  public formGroup: FormGroup<{
    accessGroups: FormArray<FormGroup<IAccessGroupDiscountFormGroup>>;
  }>;
  amountError = [Validators.required, Validators.min(0), Validators.max(100)];
  accessGroupSharedDiscountService = inject(AccessGroupSharedDiscountService);
  organizationService = inject(OrganizationService);
  snackbarService = inject(SnackbarService);
  accessGroupService = inject(AccessGroupService);
  public DiscountType = DiscountType;
  sharedAccessGroupDiscounts$: Observable<IAccessGroupSharedDiscountSearchResultItem[]>;
  response$: Observable<[IAccessGroupSharedDiscountSearchResultItem[], AccessGroup[]]>;

  ngOnInit() {
    this.formGroup = new FormGroup({
      accessGroups: new FormArray<FormGroup<IAccessGroupDiscountFormGroup>>([], allOrNoneSetValidator),
    });

    const pageEvent = new PageEvent();
    pageEvent.pageSize = 100;
    const searchEvent = new AccessGroupSearchEvent(pageEvent);
    searchEvent.organizationId = this.data.organizationId;
    this.accessGroups$ = this.accessGroupService.getAccessGroups(searchEvent).pipe(map(a => a.items));

    const sharedDiscountSearchEvent: AccessGroupSharedDiscountSearchEvent = {
      paging: <PageEvent>{
        pageIndex: 0,
        pageSize: 1000,
      },
      discountType: this.data.existingDiscountType == DiscountType.Percentages ? DiscountType.FixedAmount : DiscountType.Percentages,
      sorting: null,
      query: null,
    };

    this.sharedAccessGroupDiscounts$ = this.accessGroupSharedDiscountService
      .getSharedDiscounts(sharedDiscountSearchEvent)
      .pipe(map(x => x.items));

    this.response$ = combineLatest([this.sharedAccessGroupDiscounts$, this.accessGroups$]).pipe(
      tap(res => {
        const accessGroups = res[1];
        accessGroups.forEach(accessGroup => {
          const formGroup = new FormGroup<IAccessGroupDiscountFormGroup>({
            amount: new FormControl<number | null>(null, this.amountError),
            sharedAccessGroupDiscountIdentifier: new FormControl<string | null>(null),
          });

          formGroup.valueChanges.subscribe(value => {
            if (value.sharedAccessGroupDiscountIdentifier != null) {
              formGroup.controls.amount.disable({ onlySelf: true, emitEvent: false });
              formGroup.controls.amount.setErrors(this.amountError);
            } else {
              formGroup.controls.amount.enable({ onlySelf: true, emitEvent: false });
              formGroup.controls.amount.setErrors(null);
            }
          });

          this.formGroup.controls.accessGroups.push(formGroup);

          return res;
        });
      })
    );
  }

  submit() {
    if (!this.formGroup.valid) {
      return;
    }

    this.accessGroups$
      .pipe(
        take(1),
        mergeMap(accessGroups => {
          return this.organizationService.updateDiscountType({
            organizationId: this.data.organizationId,
            discountType: this.data.existingDiscountType == DiscountType.Percentages ? DiscountType.FixedAmount : DiscountType.Percentages,
            accessGroupAmountUpdateRequest: this.formGroup.controls.accessGroups.value.map((value, index) => ({
              identifier: accessGroups[index].identifier,
              amount: value.amount ?? null,
              sharedAccessGroupDiscountIdentifier: value.sharedAccessGroupDiscountIdentifier ?? null,
            })),
          });
        }),
        map(_ => true),
        catchError((httpError: HttpErrorResponse) => {
          if (httpError.status == 404 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 100104) {
              this.snackbarService.warning(`Organisationen kunne ikke findes.`);
            }
          }

          if (httpError.status == 400 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 100121) {
              this.snackbarService.warning(`Der mangler nye værdier for alle adgangsgrupper for organisationen.`);
            }
          }
          return of(false);
        })
      )
      .subscribe(success => {
        if (success) {
          this.dialogRef.close();
        }
      });
  }

  close() {
    this.dialogRef.close();
  }
}

function allOrNoneSetValidator(control: AbstractControl): ValidationErrors | null {
  const formArray = control as FormArray<FormGroup<IAccessGroupDiscountFormGroup>>;
  const controls = formArray.controls;
  const isAnySet = controls.some(ctrl => {
    const v = ctrl.value;
    return v.amount != null || (v.sharedAccessGroupDiscountIdentifier != null && v.sharedAccessGroupDiscountIdentifier !== '');
  });
  if (!isAnySet) return null; // valid if none are set
  const isAllSet = controls.every(ctrl => {
    const v = ctrl.value;
    return v.amount != null || (v.sharedAccessGroupDiscountIdentifier != null && v.sharedAccessGroupDiscountIdentifier !== '');
  });
  return isAllSet ? null : { allOrNoneSet: true };
}
